import { Injectable } from "@angular/core";

import { AppState } from "../app-state";

import {
  pathWaypointSchema,
  pathWaypointPullQueryBuilder,
} from "../schema/path-waypoint-schema";
import { RxDocument } from "rxdb";
import {
  MapWaypoint,
  PathWaypoint,
  sortByNumericWaypointOrder,
  WaypointTableRow,
} from "../models";
import { TestbedRxdbGraphQlCollectionService } from "./testbed-rxdb/testbed-rxdb-graphql-collection.service";
import { TestbedRxdbState } from "./testbed-rxdb/testbed-rxdb-state";
import { AuthenticationService } from "./authentication.service";
import { ColorScheme } from "../color-scheme";
import LatLonEllipsoidal_Vincenty from "geodesy/latlon-ellipsoidal-vincenty";
import { RestClient } from "./rest-client";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import _ from "lodash";
import { PointService } from "./point.service";
import { PathViewService } from "./path-view.service";
import { PointDoc } from "../schema/point-schema";

class PathWayRestClient extends RestClient<PathWaypoint> {
  constructor(http: HttpClient, appState: AppState) {
    super(http, "pathwaypoint", appState);
  }
}

export type PathWaypointDoc = RxDocument<PathWaypoint, {}>;

@Injectable({ providedIn: "root" })
export class PathWaypointService extends TestbedRxdbGraphQlCollectionService<
  PathWaypoint,
  PathWaypointDoc
> {
  restClient: PathWayRestClient;
  constructor(
    appState: AppState,
    authService: AuthenticationService,
    rxdbState: TestbedRxdbState,
    http: HttpClient,
    public pointService: PointService,
    public pathService: PathViewService
  ) {
    super(
      "path_waypoint",
      pathWaypointSchema,
      appState,
      rxdbState,
      authService
    );
    this.syncConfig = {
      push: false,
      pull: true,
      intervalMs: appState.settings.get(
        "rxdb.collections.path_waypoint.intervalMs",
        0
      ),
      retry: true,
      bGraphQL: true,
      query: pathWaypointPullQueryBuilder(),
      graphQLSubscription: `subscription{
              pathWaypointUpdated{
                pathwaypoints{
                  _id
                }
              }
            }`,
    };
    this.restClient = new PathWayRestClient(http, appState);
  }

  async filterByPathId(id: string): Promise<PathWaypointDoc[]> {
    console.log("filterByPathId() " + JSON.stringify(id));
    let query = this.rxCollection.find().where("pathId").eq(id);
    return (await query.exec()).sort(sortByNumericWaypointOrder);
  }

  updatePathContext(mapPoints: MapWaypoint[]) {
    const sortFn = (a, b) =>
      sortByNumericWaypointOrder(a.pathWaypoint, b.pathWaypoint);
    const startPts = mapPoints?.sort(sortFn).filter((tp) => !tp.parentId);
    startPts.forEach((startPt) =>
      this.updateWaypointContext(startPt, mapPoints)
    );
  }

  // Map Plotter control needs parentId to be set for each point
  updateWaypointContext(startPt: MapWaypoint, mapPoints: MapWaypoint[]) {
    if (!startPt || !mapPoints) {
      return;
    } else if (startPt.id === null) {
      const errMsg = "Invalid path test point data (missing id or order index)";
      console.warn(errMsg);
      console.dir(startPt);
      throw new Error(`${errMsg}. Point ${startPt.id}`);
    }

    try {
      startPt.color = ColorScheme.pointColorForIndex(
        Number(startPt.pathWaypoint.waypointOrder)
      );

      var childPts = mapPoints.filter(
        (pt) => pt.pathWaypoint.fromPointId == startPt.pointId
      );

      for (var childPt of childPts) {
        childPt.parentId = startPt.id;
        childPt.parentColor = startPt.color;
        childPt.parentName = startPt.name;
        const parentLat = Number(startPt.latitude);
        const parentLng = Number(startPt.longitude);
        const lat = Number(childPt.latitude);
        const lng = Number(childPt.longitude);
        if (
          !isNaN(parentLat) &&
          !isNaN(parentLng) &&
          !isNaN(lat) &&
          !isNaN(lng)
        ) {
          const parentVincenty = new LatLonEllipsoidal_Vincenty(
            parentLat,
            parentLng
          );
          const ptVincenty = new LatLonEllipsoidal_Vincenty(lat, lng);
          childPt.parentBearing = parentVincenty.finalBearingTo(ptVincenty);
          childPt.parentHorizontalDistance =
            parentVincenty.distanceTo(ptVincenty);
        } else {
          childPt.parentBearing = null;
          childPt.parentHorizontalDistance = null;
        }
        //console.log(`updateWaypointContext(${startPt.name} ${startPt.pointId}) - processing child: ${childPt.pointId}`)
        this.updateWaypointContext(childPt, mapPoints);
      }
    } catch (error) {
      console.error("Failed computing path segment lat/lng values");
      throw error;
    }
  }

  update(pathWayPoint: PathWaypoint) {
    return this.restClient.http.patch(this.restClient.urlPath, pathWayPoint, {
      observe: "response",
    });
  }

  async partialUpdate(
    pathWayPointChanges:
      | (Partial<PathWaypoint> & { _id: string })
      | (Partial<PathWaypoint> & { _id: string }[])
  ) {
    return await firstValueFrom(
      this.restClient.http.patch(
        this.restClient.urlPath + "/partialUpdate",
        pathWayPointChanges,
        { observe: "response" }
      )
    );
  }

  async findBuilding3dPaths(buildingId: string) {
    const pathPoints = await this.pathService.rxCollection.find().where('buildingId').eq(buildingId).exec();
    const paths = Object.entries(_.groupBy(pathPoints, 'pathId')).map(([pathId, pathPoints]) => ({
      pathId,
      pathPoints
    }));
    const rtnPaths: { pathId: string; mapWaypointRows: WaypointTableRow[] }[] = [];
    for (const path of paths) {
      const waypointIds = path.pathPoints.map((p) => `${p.waypointId}`);
      const waypoints = await this.getByIds(waypointIds);
      // console.log(`findBuilding3dPaths(${buildingId}) - processing path ${path.pathId} - found ${waypoints.length} waypoints`,waypoints);
      waypoints.sort(sortByNumericWaypointOrder);
      const mapWaypoints = waypoints.filter(wp=>!!wp).map(
        (wp) => new MapWaypoint(wp.toJSON(), null)
      );
      this.updatePathContext(mapWaypoints);
      const mapWaypointRows = await Promise.all(mapWaypoints.map(
        async (mwp) => {
          const mwpRow = new WaypointTableRow(mwp);
          const pointDoc = await this.pointService.getById(mwp.pointId);
          // console.log(`pointDoc: ${pointDoc?.toJSON()}`);
          mwpRow.setPoint(pointDoc?.toJSON());
          return mwpRow;
        }
      ));
      // hack to fix missing via altitude
      for ( const mwpRow of mapWaypointRows) {
        if ( mwpRow.type === 'VIA' && mwpRow.altitudeEllipsoidMeters == null) {
          // just find any pt with a valid elevation for now
          mwpRow.altitudeEllipsoidMeters = mapWaypointRows.find(mwp=>mwp.altitudeEllipsoidMeters!=null)?.altitudeEllipsoidMeters;
        }
      }
      rtnPaths.push({ pathId: path.pathId, mapWaypointRows });
    }
    return rtnPaths;
  }
}
